import React, { JSX, useEffect } from "react";
import { Typography, Box } from "@mui/material";
import moment from "moment-timezone";
import { push } from "connected-react-router";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { spFullNameSelector } from "../../../reducers/serviceProvider";
import { getBookingsForDashboard } from "../../../actions/booking";
import { Booking } from "../../../interfaces/BookingInterfaces";
import { toStartCase } from "../../../helpers/formatters";

const START_HOUR = 5; // 5 AM
const END_HOUR = 22; // 10 PM
const INTERVAL_MIN = 15;
const SLOTS_PER_HOUR = 60 / INTERVAL_MIN;
const TOTAL_HOURS = END_HOUR - START_HOUR + 1;
const TOTAL_SLOTS = TOTAL_HOURS * SLOTS_PER_HOUR;
const LEGENDS = {
  AVAILABLE: "#72C4A0",
  BOOKED: "#FBBF24",
  UNAVAILABLE: "#dedede"
};

type SlotStatus = "AVAILABLE" | "BOOKED" | "UNAVAILABLE";

interface AvailableHour {
  start: string;
  end: string;
}

const generateAvailabilityData = (
  totalSlots: number,
  doctorBookings: Booking[],
  availableHours: AvailableHour[]
): SlotStatus[] => {
  const data: SlotStatus[] = new Array(totalSlots).fill("UNAVAILABLE");

  const parsedBookings = doctorBookings.map((booking) => {
    const startMoment = moment(booking.start);
    const endMoment = moment(booking.end);

    return {
      start: startMoment.toDate(),
      end: endMoment.toDate()
    };
  });

  const today = moment().startOf("day");

  for (let i = 0; i < totalSlots; i += 1) {
    const slotStart = today.clone().add(START_HOUR * 60 + i * INTERVAL_MIN, "minutes");

    const slotEnd = slotStart.clone().add(INTERVAL_MIN, "minutes");

    const isInAvailableHours = availableHours.some(({ start, end }) => {
      const availableStart = moment(today).set({
        hour: Number(start.split(":")[0]),
        minute: Number(start.split(":")[1])
      });
      const availableEnd = moment(today).set({
        hour: Number(end.split(":")[0]),
        minute: Number(end.split(":")[1])
      });

      return slotStart.isSameOrAfter(availableStart) && slotEnd.isSameOrBefore(availableEnd);
    });

    if (!isInAvailableHours) {
      data[i] = "UNAVAILABLE";
      // eslint-disable-next-line no-continue
      continue;
    }

    data[i] = "AVAILABLE";

    // Check booking conflicts to mark as booked
    // eslint-disable-next-line no-restricted-syntax
    for (const booking of parsedBookings) {
      if (slotStart.toDate() < booking.end && slotEnd.toDate() > booking.start) {
        data[i] = "BOOKED";
        break;
      }
    }
  }

  return data;
};

const PRE_CALCULATED_TIMES = Array.from({ length: TOTAL_SLOTS }).map((_, index) => {
  const startTime = new Date();
  startTime.setHours(START_HOUR, 0, 0, 0);

  const minutesToAdd = index * INTERVAL_MIN;
  startTime.setMinutes(startTime.getMinutes() + minutesToAdd);

  return startTime.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit"
  });
});

const ScheduleRow = ({
  doctorName,
  totalSlots,
  bookings,
  availableHours,
  onSlotClick
}: {
  doctorName: string;
  totalSlots: number;
  bookings: Booking[];
  availableHours: AvailableHour[];
  onSlotClick: (startTime: string) => void;
}): JSX.Element => {
  const availabilityData = React.useMemo(
    () => generateAvailabilityData(totalSlots, bookings, availableHours),
    [totalSlots, bookings, availableHours]
  );

  return (
    <Box sx={{ display: "flex", height: 30, mb: 0.5, whiteSpace: "nowrap" }}>
      {availabilityData.map((status, index) => {
        const timeString = PRE_CALCULATED_TIMES[index];
        return (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={`${status}-${index}`}
            role={status === "AVAILABLE" ? "button" : undefined}
            title={`${doctorName}: ${timeString} - ${status}`}
            onClick={() => {
              if (status === "AVAILABLE") onSlotClick(timeString);
            }}
            sx={{
              flex: 1,
              height: "100%",
              mx: "0.5px",
              borderRadius: "2px",
              cursor: status === "AVAILABLE" ? "pointer" : "default",
              bgcolor: LEGENDS[status],
              "&:hover": {
                filter: status === "AVAILABLE" ? "brightness(0.9)" : "none"
              }
            }}
          />
        );
      })}
    </Box>
  );
};

const generateTimeLabels = () => {
  const labels = [];
  for (let hour = 5; hour <= 22; hour += 1) {
    // 5 AM to 10 PM
    if (hour === 22) {
      labels.push("10 PM"); // Label for the last hour
    } else if (hour < 12) {
      labels.push(`${hour} AM`);
    } else if (hour === 12) {
      labels.push("12 PM");
    } else {
      labels.push(`${hour - 12} PM`);
    }
  }
  return labels;
};

const ServiceProviderScheduleChart = ({
  serviceProviders,
  onClose,
  onSlotClick,
  weekDay
}: {
  serviceProviders: ServiceProvider[];
  onClose: () => void;
  onSlotClick?: (startTime: string) => void;
  weekDay?: number;
}): React.ReactNode => {
  const dispatch = useAppDispatch();
  const timeLabels = generateTimeLabels();
  const intervalsPerHour = 60 / INTERVAL_MIN;
  const bookings = useAppSelector((state) => state.dashboard);

  const totalSlots = (END_HOUR - START_HOUR + 1) * intervalsPerHour;

  return (
    <Box sx={{ overflowX: "auto", pb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          mb: 2
        }}
      >
        {Object.keys(LEGENDS).map((legend) => (
          <Box key={legend} sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Box
              sx={{
                height: "12px",
                width: "10px",
                borderRadius: "3px",
                backgroundColor: LEGENDS[legend]
              }}
            />
            <Typography variant="subtitle1">{toStartCase(legend)}</Typography>
          </Box>
        ))}
      </Box>
      {/* Header Row: Empty corner + Time Labels */}
      <Box sx={{ display: "flex", borderBottom: "1px solid #eee", pb: 0.5, mb: 0.5 }}>
        <Box sx={{ width: 180, flexShrink: 0 }} />
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            justifyContent: "space-between",
            pr: "2px"
          }}
        >
          {/* time labels */}
          {timeLabels.map(
            (label, index) =>
              (index % 2 === 0 || index === timeLabels.length - 1) && (
                <Typography
                  key={label}
                  variant="caption"
                  sx={{
                    flex: "1 0 auto",
                    textAlign: "left",
                    ml: `${index === 0 ? 0 : 4}px`,
                    minWidth: "20px",
                    fontWeight: index % 4 === 0 ? "bold" : "normal"
                  }}
                >
                  {label}
                </Typography>
              )
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex" }}>
        {/* sp Names Column */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: 180,
            flexShrink: 0,
            mt: 2
          }}
        >
          {serviceProviders.map((doctor, i) => (
            <Typography
              key={doctor.id}
              variant="subtitle2"
              sx={{
                height: 30,
                fontWeight: i % 2 === 0 ? 500 : 700,
                display: "flex",
                alignItems: "center",
                pl: 2,
                mb: 0.5
              }}
            >
              {spFullNameSelector(doctor)}
            </Typography>
          ))}
        </Box>

        {/* Schedule Rows Container */}
        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, mt: 2 }}>
          {serviceProviders.map((doctor) => {
            const selectedDay = weekDay ?? moment().weekday();
            let availableHours = doctor.schedule?.weekDays?.[selectedDay]?.hours || [];
            if (!doctor.schedule?.weekDays?.[selectedDay]?.isAvailable) {
              availableHours = [];
            }
            return (
              <ScheduleRow
                key={doctor.id}
                bookings={(bookings || []).filter((item) => item.serviceProviderId === doctor.id)}
                doctorName={spFullNameSelector(doctor)}
                availableHours={availableHours}
                onSlotClick={(startTime) => {
                  if (onSlotClick) {
                    onSlotClick(moment(startTime, "hh:mm A").toISOString());
                    onClose();
                  } else {
                    dispatch(
                      push(
                        `/calendar?serviceProviderId=${doctor.id}&startDate=${moment(
                          startTime,
                          "hh:mm A"
                        ).toISOString()}`
                      )
                    );
                    onClose();
                  }
                }}
                totalSlots={totalSlots}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const DoctorAvailabilityChart = ({
  onClose,
  serviceProviders,
  onSlotClick,
  weekDay
}: {
  onClose: () => void;
  serviceProviders: ServiceProvider[];
  onSlotClick?: (startTime: string) => void;
  weekDay?: number;
}): JSX.Element => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const newDate = new Date();
    dispatch(getBookingsForDashboard(moment(newDate).startOf("day"), moment(newDate).endOf("day")));
  }, []);

  return (
    <ServiceProviderScheduleChart
      onClose={onClose}
      serviceProviders={serviceProviders.filter((item: ServiceProvider) => item.active)}
      onSlotClick={onSlotClick}
      weekDay={weekDay}
    />
  );
};

export default DoctorAvailabilityChart;
